import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Instagram, Send, Loader2, Check } from "lucide-react";
import { SunriseCircle } from "../components/icons/SunriseCircle";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { BRAND, IMAGES } from "../data/content";
import { FadeUp } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", package_interest: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSent(true);
      setForm(initial);
      toast.success("Your message is on its way. Noelle will be in touch soon.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-brand-cream">
      <section className="pt-40 md:pt-52 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16 items-start">
          {/* left */}
          <div className="md:col-span-5">
            <FadeUp><span className="overline">Let's Begin Together</span></FadeUp>
            <FadeUp delay={0.1}>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-brand-forest tracking-tight leading-[0.95] mt-6">
                This is your beginning.
              </h1>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-brand-inkSoft text-lg leading-relaxed mt-8 max-w-md">
                Share where you are and where you long to be. Every journey starts with a single,
                honest conversation — there's no need to have it all figured out.
              </p>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="mt-12 space-y-5">
                <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full bg-brand-beige flex items-center justify-center group-hover:bg-brand-forest transition-colors duration-300">
                    <Mail size={18} className="text-brand-forest group-hover:text-brand-cream transition-colors" />
                  </span>
                  <span className="text-brand-inkSoft lowercase group-hover:text-brand-forest transition-colors">{BRAND.email.toLowerCase()}</span>
                </a>
                <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full bg-brand-beige flex items-center justify-center group-hover:bg-brand-forest transition-colors duration-300">
                    <Instagram size={18} className="text-brand-forest group-hover:text-brand-cream transition-colors" />
                  </span>
                  <span className="text-brand-inkSoft group-hover:text-brand-forest transition-colors">{BRAND.instagram}</span>
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.4}>
              <div className="mt-12 arch overflow-hidden aspect-[4/3] max-w-sm">
                <img src={IMAGES.floral} alt="Floral arrangement" className="w-full h-full object-cover" />
              </div>
            </FadeUp>
          </div>

          {/* form */}
          <div className="md:col-span-7">
            <FadeUp delay={0.2}>
              <div className="bg-brand-beige/40 rounded-[2rem] p-8 md:p-12">
                {sent ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                    data-testid="contact-success"
                  >
                    <div className="w-16 h-16 rounded-full bg-brand-forest mx-auto flex items-center justify-center">
                      <Check className="text-brand-goldBright" size={30} />
                    </div>
                    <h3 className="font-serif text-4xl text-brand-forest mt-8">Thank you.</h3>
                    <p className="text-brand-inkSoft mt-4 max-w-sm mx-auto">
                      Your message has been received. Noelle will reach out personally to begin the conversation.
                    </p>
                    <button
                      data-testid="contact-reset"
                      onClick={() => setSent(false)}
                      className="mt-8 text-brand-forest link-underline text-sm"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                    <div className="flex items-center gap-3 mb-2">
                      <SunriseCircle className="text-brand-gold" size={24} strokeWidth={1.4} />
                      <span className="overline">Begin the conversation</span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-brand-forest text-sm">Your name *</Label>
                        <Input
                          id="name"
                          data-testid="input-name"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg"
                          placeholder="Jane Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-brand-forest text-sm">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="input-email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg"
                          placeholder="you@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-brand-forest text-sm">Phone (optional)</Label>
                        <Input
                          id="phone"
                          data-testid="input-phone"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                      <div>
                        <Label className="text-brand-forest text-sm">I'm drawn to…</Label>
                        <Select value={form.package_interest} onValueChange={(v) => update("package_interest", v)}>
                          <SelectTrigger
                            data-testid="select-package"
                            className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus:ring-0 text-brand-forest text-lg"
                          >
                            <SelectValue placeholder="Select an experience" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="discovery">A free discovery call</SelectItem>
                            <SelectItem value="renew">The Renew Experience (90-Day)</SelectItem>
                            <SelectItem value="flourish">The Flourish Experience (6-Month)</SelectItem>
                            <SelectItem value="unsure">I'm not sure yet</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-brand-forest text-sm">What's the story in your head? *</Label>
                      <Textarea
                        id="message"
                        data-testid="input-message"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        rows={5}
                        className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg resize-none"
                        placeholder="Share where you are, and where your heart wants to go…"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="group inline-flex items-center gap-3 bg-brand-forest text-brand-cream px-10 py-4 rounded-full text-sm tracking-wide hover:-translate-y-1 hover:shadow-2xl transition-transform duration-300 disabled:opacity-60"
                    >
                      {loading ? (
                        <><Loader2 size={18} className="animate-spin" /> Sending…</>
                      ) : (
                        <>Send my message <Send size={16} className="group-hover:translate-x-1 transition-transform duration-300" /></>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
